import {MessageKey} from '@/i18n'
import {
    ACCEPT_ATTRIBUTE as IMAGE_ACCEPT, formatBytes, ImageErrorCode,
    MAX_SIDE, MAX_STORED_BYTES, prepareImage
} from '@/utils/imageFile'
import {
    ACCEPT_ATTRIBUTE as AUDIO_ACCEPT, AudioErrorCode, MAX_BYTES as AUDIO_MAX_BYTES, prepareAudio
} from '@/utils/audioFile'

/**
 * O que o campo de mídia precisa saber para cada tipo, e o despacho da validação.
 *
 * Existe para que o MediaField tenha UM ramo de template só — o preview — e todo o
 * resto (aceite, ícones, textos, limites) venha por valor. Sem isto o campo viraria
 * uma cadeia de ifs por tipo, que é exatamente o que o PropertyValue já é e o que
 * não se quer repetir.
 */

export type MediaErrorCode = ImageErrorCode | AudioErrorCode

export type PrepareMediaResult =
    | { ok: true; blob: Blob; duration?: number }
    | { ok: false; code: MediaErrorCode }

export type MediaKind = {
    accept: string
    emptyIcon: string
    maxBytes: number
    /** altura da caixa: a imagem precisa de área, o áudio é uma faixa */
    boxHeight: string
    dropHintKey: MessageKey
    emptyKey: MessageKey
    hintKey: MessageKey
    removeKey: MessageKey
    hintValues(): Record<string, any>
    errorKeys: Record<string, MessageKey>
}

export const MEDIA_KINDS: Record<string, MediaKind> = {
    image: {
        accept: IMAGE_ACCEPT,
        emptyIcon: 'mdi-image-plus',
        maxBytes: MAX_STORED_BYTES,
        boxHeight: '180px',
        dropHintKey: 'image.drop-hint',
        emptyKey: 'image.empty',
        hintKey: 'image.hint',
        removeKey: 'image.remove',
        hintValues: () => ({size: formatBytes(MAX_STORED_BYTES), side: MAX_SIDE}),
        errorKeys: {
            'image-type': 'validation.image-type',
            'image-too-large': 'validation.image-too-large',
            'image-decode': 'validation.image-decode'
        }
    },
    audio: {
        accept: AUDIO_ACCEPT,
        emptyIcon: 'mdi-music-note-plus',
        maxBytes: AUDIO_MAX_BYTES,
        boxHeight: '96px',
        dropHintKey: 'audio.drop-hint',
        emptyKey: 'audio.empty',
        hintKey: 'audio.hint',
        removeKey: 'audio.remove',
        hintValues: () => ({size: formatBytes(AUDIO_MAX_BYTES)}),
        errorKeys: {
            'audio-type': 'validation.audio-type',
            'audio-too-large': 'validation.audio-too-large',
            'audio-decode': 'validation.audio-decode'
        }
    }
}

export function mediaKindOf(type: string): MediaKind {
    return MEDIA_KINDS[type] || MEDIA_KINDS.image
}

export async function prepareMedia(file: File, type: string): Promise<PrepareMediaResult> {
    if (type === 'audio') return await prepareAudio(file)
    return await prepareImage(file)
}

export default {MEDIA_KINDS, mediaKindOf, prepareMedia}
